import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  HiOutlineBell,
  HiOutlineCheck,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineDocumentText,
  HiOutlineLockOpen,
  HiOutlineXCircle,
} from "react-icons/hi";
import useNotifications from "../hooks/useNotifications";
import {
  formatBadgeCount,
  formatRelativeTime,
  getNotificationText,
} from "../utils/notificationUtils";

const EVENT_STYLES = {
  submission_pending: {
    Icon: HiOutlineClock,
    className: "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300",
  },
  submission_approved: {
    Icon: HiOutlineCheck,
    className: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300",
  },
  submission_fully_approved: {
    Icon: HiOutlineCheckCircle,
    className: "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300",
  },
  submission_rejected: {
    Icon: HiOutlineXCircle,
    className: "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300",
  },
  submission_unlocked: {
    Icon: HiOutlineLockOpen,
    className: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300",
  },
};

const QUOTATION_STYLE = {
  Icon: HiOutlineDocumentText,
  className: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300",
};

const DEFAULT_STYLE = {
  Icon: HiOutlineBell,
  className: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300",
};

const getEventStyle = (notification) => {
  if (EVENT_STYLES[notification.event]) return EVENT_STYLES[notification.event];
  if (notification.module === "timesheet") return DEFAULT_STYLE;
  return QUOTATION_STYLE;
};

const NotificationRow = ({ notification, now, onClick }) => {
  const { heading, body } = getNotificationText(notification);
  const { Icon, className: iconClassName } = getEventStyle(notification);
  const isUnread = !notification.isRead;

  return (
    <button
      type="button"
      onClick={() => onClick(notification)}
      className={`w-full flex items-start gap-3 px-4 py-3 text-left text-sm border-b border-gray-100 dark:border-gray-700 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/60 ${
        isUnread ? "bg-blue-50/70 dark:bg-blue-900/20" : ""
      }`}
    >
      <span
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${iconClassName}`}
      >
        <Icon size={18} />
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={`block break-words ${
            isUnread
              ? "font-semibold text-gray-900 dark:text-white"
              : "font-medium text-gray-600 dark:text-gray-300"
          }`}
        >
          {heading || "การแจ้งเตือน"}
        </span>
        {body && (
          <span
            className={`mt-0.5 block break-words ${
              isUnread ? "text-gray-700 dark:text-gray-200" : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {body}
          </span>
        )}
        <span
          className="mt-1 block text-xs text-gray-400 dark:text-gray-500"
          title={
            notification.createdAt
              ? new Date(notification.createdAt).toLocaleString("th-TH")
              : undefined
          }
        >
          {formatRelativeTime(notification.createdAt, now)}
        </span>
      </span>

      {isUnread && (
        <span
          className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500"
          aria-label="ยังไม่อ่าน"
        />
      )}
    </button>
  );
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const openLatestRef = useRef(null);
  const [now, setNow] = useState(() => Date.now());

  const {
    items,
    unreadCount,
    isOpen,
    loading,
    loadingMore,
    error,
    hasMore,
    close,
    toggle,
    refreshList,
    loadMore,
    openNotification,
    openLatestNotification,
    markAllAsRead,
  } = useNotifications({
    onNavigate: (link) => navigate(link),
    onRealtimeNotification: (payload) => {
      const { heading, body } = getNotificationText(payload);
      toast.info(
        <div>
          <div className="font-semibold">{heading || "การแจ้งเตือนใหม่"}</div>
          {body && <div className="mt-0.5 text-sm">{body}</div>}
          <div className="mt-1 text-xs opacity-80">คลิกเพื่อเปิด</div>
        </div>,
        {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
          onClick: () => openLatestRef.current?.(),
        }
      );
    },
  });
  openLatestRef.current = openLatestNotification;

  // ให้เวลา relative ขยับเองระหว่างที่ dropdown เปิดอยู่
  useEffect(() => {
    if (!isOpen) return undefined;
    setNow(Date.now());
    const intervalId = window.setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => window.clearInterval(intervalId);
  }, [isOpen, items]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        close();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, close]);

  const handleMarkAllAsRead = async () => {
    const success = await markAllAsRead();
    if (!success) toast.error("ทำเครื่องหมายอ่านทั้งหมดไม่สำเร็จ");
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggle}
        className="relative text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        aria-label={
          unreadCount > 0 ? `การแจ้งเตือน ยังไม่อ่าน ${unreadCount} รายการ` : "การแจ้งเตือน"
        }
        aria-expanded={isOpen}
      >
        <HiOutlineBell size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs min-w-[1.25rem] h-5 px-1 rounded-full flex items-center justify-center">
            {formatBadgeCount(unreadCount)}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-lg z-50 overflow-hidden transition-colors duration-300">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 font-semibold text-gray-700 dark:text-gray-100 flex justify-between items-center">
            <span>
              การแจ้งเตือน
              {unreadCount > 0 && (
                <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                  ยังไม่อ่าน {unreadCount}
                </span>
              )}
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="text-xs text-blue-500 hover:underline"
              >
                อ่านทั้งหมด
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && items.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                กำลังโหลด...
              </p>
            ) : items.length > 0 ? (
              items.map((notification) => (
                <NotificationRow
                  key={notification._id}
                  notification={notification}
                  now={now}
                  onClick={openNotification}
                />
              ))
            ) : !error ? (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                ไม่มีการแจ้งเตือน
              </p>
            ) : null}

            {error && (
              <div className="p-3 text-center text-sm text-red-500">
                {error}
                <button
                  type="button"
                  onClick={items.length > 0 && hasMore ? loadMore : refreshList}
                  className="ml-2 text-blue-500 hover:underline"
                >
                  ลองใหม่
                </button>
              </div>
            )}

            {hasMore && !error && (
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full py-2.5 text-sm text-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700/60 disabled:opacity-60"
              >
                {loadingMore ? "กำลังโหลด..." : "ดูเพิ่มเติม"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
